import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useState, useEffect } from 'react';
import { useSetJoyHotelDiscountByRule } from '../HotelsDiscountsProvider';

/**
 * strikeout price is our competitors price
 * advertised price is our price
 *
 * Returns true if any price has a strikeout price that is greater than or equal to the advertised price.
 * This means the user is already seeing some savings (or at least not paying more than the strikeout price).
 * Prices without a strikeout price are ignored.
 */
function hasSomeSavings(prices: ReadonlyArray<Readonly<HotelPricingData>>) {
  return prices.some(price => {
    if (typeof price.strikeoutPrice !== 'number') {
      // Ignore prices without strikeout pricing
      return false;
    }
    // User is already getting a deal if strikeout >= advertised
    return price.strikeoutPrice >= price.advertisedPrice;
  });
}

export type HotelPricingData = Readonly<{
  /** The discounted/final price shown to the user */
  advertisedPrice: number;
  /** The original price (strikeout price) before discount, if any */
  strikeoutPrice: number | null | undefined;
}>;

type HotelPricingRegistrationFunction = (prices: ReadonlyArray<HotelPricingData>) => void;

/**
 * Custom hook to register hotel pricing data and apply discount rules.
 *
 * This hook exposes the user to the 'atLeastSomeSavings' discount experiment only if
 * none of the prices show legitimate savings (strikeout price >= advertised price).
 *
 * The validation ensures that (in order of precedence):
 * 1. If the user has opted out of TripProfit (websiteOptOutTripProfit), the discount is NOT applied.
 *    This check takes precedence over all other conditions, including testing flags.
 * 2. If the feature flag 'forceHotelDiscountAtLeastSomeSavings' is enabled (for testing), the discount is always applied
 *    (only if opt-out check passes).
 * 3. If any price with a strikeout price is greater than or equal to the advertised price,
 *    the user is already getting a deal and is NOT exposed to the experiment.
 * 4. Prices without a strikeout price are ignored in validation.
 *
 * @returns An object containing:
 *   - `registerHotelPrices`: A callback function that accepts an array of hotel pricing data
 *     and exposes the user to the discount experiment only if no savings are present.
 *
 * @example
 * const { registerHotelPrices } = useRegisterHotelPrices();
 * registerHotelPrices([
 *   { advertisedPrice: 80, strikeoutPrice: 100 },   // Already has savings, not exposed
 *   { advertisedPrice: 120, strikeoutPrice: 150 },  // Already has savings, not exposed
 *   { advertisedPrice: 200, strikeoutPrice: 200 },  // Already has savings, not exposed
 *   { advertisedPrice: 90, strikeoutPrice: null },  // Ignored
 * ]);
 */
export const useRegisterHotelPrices = () => {
  const setJoyHotelDiscountByRule = useSetJoyHotelDiscountByRule();
  const [forceAtLeastSomeSavings] = useIsFeatureEnabled('forceHotelDiscountAtLeastSomeSavings');
  const [isTripProfitOptedOut, isTripProfitOptedOutLoading] = useIsFeatureEnabled({ consentKey: 'websiteOptOutTripProfit' });
  const [exposed, setShouldExpose] = useState<boolean>(false);
  const [isFeatureEnabled] = useIsFeatureEnabled('travelHotelCouponsAccommodationItem', { skip: !exposed });

  const registerHotelPrices: HotelPricingRegistrationFunction = useEventCallback(
    (prices: ReadonlyArray<HotelPricingData>) => {
      // Don't apply discount if user has opted out OR consent is still loading
      // Loading check prevents race condition where discount briefly applies before consent loads
      if (isTripProfitOptedOut || isTripProfitOptedOutLoading) {
        return;
      }

      // If force flag is enabled, always apply discount
      if (forceAtLeastSomeSavings) {
        setJoyHotelDiscountByRule?.('atLeastSomeSavings');
        return;
      }

      // If the user already has some savings, do not expose to experiment
      const hasSomeSavingsAlready = hasSomeSavings(prices);
      if (hasSomeSavingsAlready) {
        return;
      }
      // User has no savings - expose to discounts experiment
      setShouldExpose(true);
    },
    [setJoyHotelDiscountByRule, forceAtLeastSomeSavings, isTripProfitOptedOut, isTripProfitOptedOutLoading, setShouldExpose],
  );

  // Apply the discount only if the feature flag is enabled and user hasn't opted out
  useEffect(() => {
    // Don't apply discount if user has opted out OR consent is still loading
    if (isTripProfitOptedOut || isTripProfitOptedOutLoading) {
      return;
    }

    if (exposed && isFeatureEnabled) {
      // Managed by Amplitude: https://app.amplitude.com/experiment/joylife/142907
      setJoyHotelDiscountByRule?.('atLeastSomeSavings');
    }
  }, [exposed, isFeatureEnabled, isTripProfitOptedOut, isTripProfitOptedOutLoading, setJoyHotelDiscountByRule]);

  return {
    registerHotelPrices,
  };
};
